import store from '@store/index'
import {
  agencyBillingRecoveryRedirect,
  isAgencyBillingExemptRouteName,
} from '@utils/agencyBillingRecoveryRedirect'
import { hasRepuAgencyAccess, isAgencySubscribed, isRepuagencyProductHost } from '@utils/subscription'
import { userUsesNewUiEligible } from '@utils/userUsesNewUiEligible'

/** RepuAgency platform billing routes — agency owners only, never business end-customers. */
function isAgencyPlatformBillingRoute(name) {
  if (!name) return false
  const routeName = String(name)
  return (
    routeName === 'agency.subscription' ||
    routeName.startsWith('agency.payment-methods')
  )
}

/** Business user who is also the agency owner (same person), not a normal end-customer. */
function businessUserOwnsAgency(user) {
  if (!user || user.role !== 'Business' || !user.business) return false
  const ownerId = user.business.owner_id ?? user.business.owner?.id
  return ownerId != null && Number(user.id) === Number(ownerId)
}

const newAgencyToLegacyMap = {
  'new-agency.dashboard': 'agency.dashboard',
  'new-agency.businesses.index': 'agency.businesses.index',
  'new-agency.customers.index': 'agency.customers.index',
  'new-agency.settings': 'agency.settings',
  'new-agency.embed': 'agency.embed',
  'new-agency.sr-widget': 'agency.sr-widget',
  'new-agency.zensocial-widget': 'agency.zensocial-widget',
  'new-agency.sf-widget': 'agency.sf-widget',
  'new-agency.instagram-widget.index': 'agency.instagram-widget.index',
  'new-agency.insta-stream-widget': 'agency.insta-stream-widget',
  'new-agency.widgets.index': 'agency.widgets.index',
  'new-agency.business-subscriptions': 'agency.business-subscriptions',
  'new-agency.subscriptions': 'agency.subscriptions',
}

function AgencyGuard(to, from, next) {
  let user = store.getters['auth/user']
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const isReputationHub = origin.includes('reputationhub.io')
  const isZenSocial = origin.includes('zensocial.io')
  /** Billing / purchase wall: RepuAgency product host only (matches Subscription.vue `isRepuagencySite`). */
  const isRepuagencyDomain = isRepuagencyProductHost()
  if (!user) {
    next({ name: 'login' })
  } else if (user.role === 'Admin') {
    // Allow Admin users to access agency routes
    next()
  } else if (
    user.role === 'Agency' &&
    to.name &&
    String(to.name).startsWith('new-agency.') &&
    !userUsesNewUiEligible(user)
  ) {
    const legacy = newAgencyToLegacyMap[to.name]
    if (legacy) {
      return next({ name: legacy, query: to.query, hash: to.hash })
    }
    return next({ name: 'agency.dashboard', query: to.query, hash: to.hash })
  } else if (user.role !== 'Agency') {
    if (user.role === 'Business' && isAgencyPlatformBillingRoute(to.name)) {
      const dashboard = userUsesNewUiEligible(user) ? 'new-business.dashboard' : 'business.dashboard'
      return next({ name: dashboard })
    }

    // Business users who are also the agency owner can use agency routes (not platform billing).
    const hasAgencyAccess = businessUserOwnsAgency(user)
    const isAgencyRoute = to.name && (String(to.name).startsWith('agency.') || String(to.name).startsWith('new-agency.'))
    const fromAgencyRoute = from.name && (String(from.name).startsWith('agency.') || String(from.name).startsWith('new-agency.'))
    const allowedForBusiness = [
      'agency.customers.index',
      'agency.dashboard',
      'agency.businesses.index',
      'agency.landing',
      'agency.settings',
      'agency.training',
      'agency.embed',
      'agency.sr-widget',
      'agency.sf-widget',
      'agency.zensocial-widget',
      'agency.instagram-widget.index',
      'agency.insta-stream-widget',
      'agency.widgets.index',
      'agency.lead-machine-dashboard',
      'agency.local-searches-mailed',
      'agency.local-searches',
      'agency.letter-templates',
      'agency.le-landing-pages',
      'agency.lead-machine-upload',
      'agency.businesses.archived',
      'agency.businesses.edit',
      'agency.customers.edit',
      'agency.businesses.create',
      'agency.profile',
      // RepuSideBarNew uses new-agency.* route names (Instagram stream, Google stream, Settings, etc.)
      'new-agency.settings',
      'new-agency.embed',
      'new-agency.sr-widget',
      'new-agency.sf-widget',
      'new-agency.zensocial-widget',
      'new-agency.instagram-widget.index',
      'new-agency.insta-stream-widget',
      'new-agency.widgets.index',
      'new-agency.business-subscriptions',
      'new-agency.subscriptions',
      'new-agency.dashboard',
      'new-agency.businesses.index',
      'new-agency.customers.index',
    ]
    if (hasAgencyAccess && isAgencyRoute) {
      if (isAgencyPlatformBillingRoute(to.name)) {
        const dashboard = userUsesNewUiEligible(user) ? 'new-business.dashboard' : 'business.dashboard'
        return next({ name: dashboard })
      }
      if (to.name && String(to.name).startsWith('new-agency.') && !userUsesNewUiEligible(user)) {
        const legacy = newAgencyToLegacyMap[to.name]
        if (legacy) {
          next({ name: legacy, query: to.query, hash: to.hash })
        } else {
          next({ name: 'agency.dashboard', query: to.query, hash: to.hash })
        }
        return
      }
      next() // allow agency routes for business users who own the agency
    } else if (fromAgencyRoute && isAgencyRoute) {
      if (user.role === 'Business' && isAgencyPlatformBillingRoute(to.name)) {
        const dashboard = userUsesNewUiEligible(user) ? 'new-business.dashboard' : 'business.dashboard'
        return next({ name: dashboard })
      }
      if (to.name && String(to.name).startsWith('new-agency.') && !userUsesNewUiEligible(user)) {
        const legacy = newAgencyToLegacyMap[to.name]
        if (legacy) {
          next({ name: legacy, query: to.query, hash: to.hash })
        } else {
          next({ name: 'agency.dashboard', query: to.query, hash: to.hash })
        }
        return
      }
      next() // already in agency section – allow navigation to any agency route
    } else if (allowedForBusiness.includes(to.name)) {
      if (user.role === 'Business' && isAgencyPlatformBillingRoute(to.name)) {
        const dashboard = userUsesNewUiEligible(user) ? 'new-business.dashboard' : 'business.dashboard'
        return next({ name: dashboard })
      }
      if (to.name && String(to.name).startsWith('new-agency.') && !userUsesNewUiEligible(user)) {
        const legacy = newAgencyToLegacyMap[to.name]
        if (legacy) {
          next({ name: legacy, query: to.query, hash: to.hash })
        } else {
          next({ name: 'agency.dashboard', query: to.query, hash: to.hash })
        }
        return
      }
      next() // allow listed agency routes (Settings, widgets, etc.) so links work even when from/hasAgencyAccess is not set
    } else {
      next('/') // block everything else
    }
  } else if (user.agency && user.agency.owner && user.agency.owner.on_trial) {
    next()
  } else if (
    user.role === 'Agency' &&
    isRepuagencyDomain &&
    !isZenSocial &&
    !user?.agency?.free_fee &&
    // Lock only for RA billing recovery, or accounts with no product entitlements at all.
    // Mini-app lifetime customers who canceled RA must still use the legacy app.
    (agencyBillingRecoveryRedirect(user) ||
      (!hasRepuAgencyAccess(user) && !isAgencySubscribed(user)))
  ) {
    // Past due / unpaid RA recovery, or no products — subscription/profile/payment only
    if (!isAgencyBillingExemptRouteName(to.name)) {
      next({ name: 'agency.subscription' })
    } else {
      next()
    }
  } else if (!isZenSocial && !isReputationHub && user.agency) {
    const isSubscribed = isAgencySubscribed(user)

    if (!isSubscribed) {
      if (newAgencyToLegacyMap[to.name]) {
        next({ name: newAgencyToLegacyMap[to.name], query: to.query, hash: to.hash })
        return
      }

      const allowedRoutesForNonSubscribers = [
        'agency.subscription',
        'agency.profile',
        'agency.settings',
        'agency.dashboard',
        'agency.customers.index',
        'agency.customers.edit',
        'agency.customers.create',
      ]
      const isAllowedRoute =
        allowedRoutesForNonSubscribers.includes(to.name) ||
        (to.name && to.name.startsWith('agency.payment-methods'))
      if (!isAllowedRoute) {
        next({ name: 'agency.subscription' })
      } else {
        next()
      }
    } else {
      next()
    }
  } else {
    next()
  }
}

export default AgencyGuard
